import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Send, Search, Loader2, CheckCircle2, XCircle, Clock, ArrowDownLeft, ArrowUpRight,
    Inbox, History, Wallet, AlertCircle, RotateCcw,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtUSD = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtCT = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        });
    } catch { return '—'; }
};

const statusBadge = (status) => {
    const map = {
        pending_accept: { label: 'Waiting to accept', color: '#FFD700', bg: 'bg-[#FFD700]/15', border: 'border-[#FFD700]/40' },
        accepted:       { label: 'Accepted',          color: '#39FF14', bg: 'bg-[#39FF14]/15', border: 'border-[#39FF14]/40' },
        declined:       { label: 'Declined',          color: '#FF3B30', bg: 'bg-[#FF3B30]/15', border: 'border-[#FF3B30]/40' },
        expired:        { label: 'Expired',           color: '#A0AAB2', bg: 'bg-white/5',      border: 'border-white/10' },
        canceled:       { label: 'Canceled',          color: '#A0AAB2', bg: 'bg-white/5',      border: 'border-white/10' },
        reversed:       { label: 'Reversed by admin', color: '#FF3B30', bg: 'bg-[#FF3B30]/15', border: 'border-[#FF3B30]/40' },
    };
    const m = map[status] || { label: status, color: '#A0AAB2', bg: 'bg-white/5', border: 'border-white/10' };
    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${m.bg} ${m.border} border`}
            style={{ color: m.color }}
        >
            {m.label}
        </span>
    );
};

const SendCredits = () => {
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const [settings, setSettings] = useState(null);
    const [email, setEmail] = useState('');
    const [lookup, setLookup] = useState(null);
    const [lookupErr, setLookupErr] = useState(null);
    const [looking, setLooking] = useState(false);
    const [amount, setAmount] = useState('');
    const [memo, setMemo] = useState('');
    const [sending, setSending] = useState(false);
    const [inbox, setInbox] = useState([]);
    const [history, setHistory] = useState([]);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        axios.get(`${API}/wallet/peer/settings`, { withCredentials: true })
            .then(({ data }) => setSettings(data))
            .catch(() => setSettings({ enabled: false }));
    }, []);

    const fetchInbox = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/wallet/peer/inbox`, { withCredentials: true });
            setInbox(Array.isArray(data) ? data : []);
        } catch { /* ignore */ }
    }, []);

    const fetchHistory = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/wallet/peer/history`, { withCredentials: true });
            setHistory(Array.isArray(data) ? data : []);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => {
        fetchInbox();
        fetchHistory();
        const id = setInterval(() => { fetchInbox(); fetchHistory(); }, 30_000);
        return () => clearInterval(id);
    }, [fetchInbox, fetchHistory]);

    const doLookup = async () => {
        if (!email.trim()) return;
        setLooking(true);
        setLookup(null);
        setLookupErr(null);
        try {
            const { data } = await axios.post(
                `${API}/wallet/peer/lookup`,
                { email: email.trim() },
                { withCredentials: true }
            );
            setLookup(data);
        } catch (e) {
            setLookupErr(e.response?.data?.detail || "Couldn't find that player");
        }
        setLooking(false);
    };

    const doSend = async () => {
        if (!lookup || !amount) return;
        const amt = parseFloat(amount);
        if (isNaN(amt) || amt <= 0) {
            toast.error('Enter a valid amount');
            return;
        }
        setSending(true);
        try {
            const { data } = await axios.post(
                `${API}/wallet/peer/send`,
                { recipient_email: lookup.email, amount: amt, memo: memo.trim() || undefined },
                { withCredentials: true }
            );
            toast.success(data.message, { duration: 6000 });
            setEmail(''); setLookup(null); setAmount(''); setMemo('');
            await fetchHistory();
            try { await refreshUser?.(); } catch { /* noop */ }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not send');
        }
        setSending(false);
    };

    const doAccept = async (tid) => {
        setBusyId(tid);
        try {
            const { data } = await axios.post(`${API}/wallet/peer/${tid}/accept`, {}, { withCredentials: true });
            toast.success(data.message);
            await fetchInbox(); await fetchHistory();
            try { await refreshUser?.(); } catch { /* noop */ }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not accept');
        }
        setBusyId(null);
    };

    const doDecline = async (tid) => {
        if (!window.confirm('Decline this transfer? Credits go back to the sender.')) return;
        setBusyId(tid);
        try {
            await axios.post(`${API}/wallet/peer/${tid}/decline`, {}, { withCredentials: true });
            toast.success('Declined — sender refunded');
            await fetchInbox(); await fetchHistory();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not decline');
        }
        setBusyId(null);
    };

    const doCancel = async (tid) => {
        if (!window.confirm('Cancel this pending transfer? Credits will come back to you.')) return;
        setBusyId(tid);
        try {
            const { data } = await axios.post(`${API}/wallet/peer/${tid}/cancel`, {}, { withCredentials: true });
            toast.success(data.message);
            await fetchHistory();
            try { await refreshUser?.(); } catch { /* noop */ }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not cancel');
        }
        setBusyId(null);
    };

    if (settings && !settings.enabled) {
        return (
            <div className="min-h-screen bg-[#050505]">
                <Header />
                <div className="max-w-2xl mx-auto px-4 py-12">
                    <div className="card-dark p-8 text-center">
                        <AlertCircle className="w-12 h-12 text-[#FFD700] mx-auto mb-4" />
                        <h2 className="text-xl font-bold text-white mb-2">Peer Transfers Paused</h2>
                        <p className="text-[#A0AAB2]">Sending credits between players is currently disabled. Please check back later.</p>
                        <Button onClick={() => navigate('/')} className="mt-4 bg-[#FFD700] text-black hover:bg-[#FFD700]/80">Back to Casino Floor</Button>
                    </div>
                </div>
            </div>
        );
    }

    const cashBal = Number(user?.credits || 0);
    const amtNum = parseFloat(amount) || 0;
    const canSend = !!lookup && amtNum >= (settings?.min_amount || 1) && amtNum <= Math.min(cashBal, settings?.max_amount_per_transfer || 500);

    return (
        <div className="min-h-screen bg-[#050505]">
            <Header />
            <div className="max-w-3xl mx-auto px-4 py-8">
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-black text-white">Send <span className="text-[#FFD700]">Credits</span></h1>
                        <p className="text-[#A0AAB2] text-sm">Share credits with another player. Cash credits only.</p>
                    </div>
                    <div className="card-dark px-4 py-2 flex items-center gap-2">
                        <Wallet className="w-4 h-4 text-[#FFD700]" />
                        <span className="text-xs text-[#A0AAB2]">Cash:</span>
                        <span className="text-base font-mono font-bold text-[#FFD700]" data-testid="peer-cash-balance">{fmtUSD(cashBal)}</span>
                    </div>
                </div>

                <Tabs defaultValue="send" className="w-full">
                    <TabsList className="bg-black/40 border border-white/10">
                        <TabsTrigger value="send" data-testid="tab-send"><Send className="w-4 h-4 mr-1" /> Send</TabsTrigger>
                        <TabsTrigger value="inbox" data-testid="tab-inbox">
                            <Inbox className="w-4 h-4 mr-1" /> Inbox
                            {inbox.length > 0 && (
                                <span className="ml-1 bg-[#FFD700] text-black text-[10px] font-black px-1.5 rounded-full">{inbox.length}</span>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="history" data-testid="tab-history"><History className="w-4 h-4 mr-1" /> History</TabsTrigger>
                    </TabsList>

                    <TabsContent value="send" className="mt-4">
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card-dark p-6 space-y-5" data-testid="send-form">
                            <div>
                                <label className="block text-sm font-bold text-white mb-2">Find your friend by email</label>
                                <div className="flex gap-2">
                                    <Input
                                        type="email"
                                        placeholder="friend@example.com"
                                        value={email}
                                        onChange={(e) => { setEmail(e.target.value); setLookup(null); setLookupErr(null); }}
                                        onKeyDown={(e) => e.key === 'Enter' && doLookup()}
                                        className="bg-black/40 border-white/10 text-white"
                                        data-testid="peer-email-input"
                                    />
                                    <Button onClick={doLookup} disabled={!email.trim() || looking} className="bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold" data-testid="peer-lookup-btn">
                                        {looking ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1" />Find</>}
                                    </Button>
                                </div>
                                {lookupErr && <p className="text-[#FF3B30] text-xs font-bold mt-2" data-testid="peer-lookup-error">{lookupErr}</p>}
                                {lookup && (
                                    <div className="mt-3 p-3 rounded-md bg-[#39FF14]/10 border border-[#39FF14]/30 flex items-center gap-2" data-testid="peer-lookup-result">
                                        <CheckCircle2 className="w-5 h-5 text-[#39FF14]" />
                                        <div>
                                            <div className="text-white font-bold">{lookup.display_name}</div>
                                            <div className="text-[10px] text-[#A0AAB2]">{lookup.email}</div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {lookup && (
                                <>
                                    <div>
                                        <label className="block text-sm font-bold text-white mb-2">Amount</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0AAB2] font-mono">$</span>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min={settings?.min_amount || 1}
                                                max={Math.min(cashBal, settings?.max_amount_per_transfer || 500)}
                                                value={amount}
                                                onChange={(e) => setAmount(e.target.value)}
                                                className="pl-7 bg-black/40 border-white/10 text-white text-2xl font-mono font-bold h-14"
                                                placeholder="0.00"
                                                data-testid="peer-amount-input"
                                                autoFocus
                                            />
                                        </div>
                                        <p className="text-[10px] text-[#A0AAB2] mt-1">
                                            Min {fmtUSD(settings?.min_amount || 1)} · Max {fmtUSD(Math.min(cashBal, settings?.max_amount_per_transfer || 500))} · Daily cap {fmtUSD(settings?.daily_sender_cap || 1000)}
                                        </p>
                                        <div className="flex gap-2 mt-2">
                                            {[5, 10, 25, 50].filter((p) => p <= cashBal).map((p) => (
                                                <button
                                                    type="button"
                                                    key={p}
                                                    onClick={() => setAmount(String(p))}
                                                    className="px-3 py-1 rounded-full bg-[#FFD700]/10 text-[#FFD700] border border-[#FFD700]/30 text-xs font-bold hover:bg-[#FFD700]/20"
                                                    data-testid={`peer-quick-${p}`}
                                                >
                                                    ${p}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-white mb-2">Memo (optional)</label>
                                        <Input
                                            type="text"
                                            value={memo}
                                            onChange={(e) => setMemo(e.target.value.slice(0, 140))}
                                            placeholder="Pizza, sports bet split, etc."
                                            className="bg-black/40 border-white/10 text-white"
                                            data-testid="peer-memo-input"
                                        />
                                    </div>

                                    <div className="rounded-md bg-[#FFD700]/5 border border-[#FFD700]/20 p-3 text-[11px] text-[#A0AAB2] flex items-start gap-2">
                                        <Clock className="w-4 h-4 text-[#FFD700] shrink-0 mt-0.5" />
                                        <span>
                                            <span className="text-white font-bold">{lookup.display_name}</span> will have <span className="text-white font-bold">{settings?.acceptance_window_hours || 24} hours</span> to accept.
                                            If they don't, the credits come right back to your balance — no risk.
                                        </span>
                                    </div>

                                    <Button
                                        onClick={doSend}
                                        disabled={!canSend || sending}
                                        className="w-full bg-[#39FF14] text-black hover:bg-[#39FF14]/80 font-black h-12 text-base"
                                        data-testid="peer-send-btn"
                                    >
                                        {sending ? (
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                        ) : (
                                            <>
                                                <Send className="w-5 h-5 mr-2" />
                                                Send {fmtUSD(amtNum)} to {lookup.display_name}
                                            </>
                                        )}
                                    </Button>
                                </>
                            )}
                        </motion.div>
                    </TabsContent>

                    <TabsContent value="inbox" className="mt-4">
                        {inbox.length === 0 ? (
                            <div className="card-dark p-8 text-center text-[#A0AAB2]" data-testid="inbox-empty">
                                <Inbox className="w-10 h-10 mx-auto mb-2 opacity-40" />
                                Nothing waiting. When a friend sends you credits, they'll show up here.
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {inbox.map((t) => (
                                    <div key={t.id} className="card-dark p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`inbox-${t.id}`}>
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-10 h-10 rounded-full bg-[#FFD700]/15 flex items-center justify-center">
                                                <ArrowDownLeft className="w-5 h-5 text-[#FFD700]" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-white font-bold truncate">{t.sender_name}</div>
                                                <div className="text-[11px] text-[#A0AAB2] truncate">
                                                    {t.sender_email}
                                                    {t.memo && <span> · "{t.memo}"</span>}
                                                </div>
                                                <div className="text-[10px] text-[#A0AAB2]">Expires {fmtCT(t.expires_at)}</div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-mono text-xl font-black text-[#FFD700]">{fmtUSD(t.amount)}</div>
                                            <div className="flex gap-1.5 mt-1">
                                                <Button size="sm" onClick={() => doAccept(t.id)} disabled={busyId === t.id} className="bg-[#39FF14] text-black hover:bg-[#39FF14]/80 font-bold" data-testid={`accept-${t.id}`}>
                                                    {busyId === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Accept</>}
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => doDecline(t.id)} disabled={busyId === t.id} className="border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10" data-testid={`decline-${t.id}`}>
                                                    <XCircle className="w-3.5 h-3.5 mr-1" />Decline
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="history" className="mt-4">
                        {history.length === 0 ? (
                            <div className="card-dark p-8 text-center text-[#A0AAB2]">No transfers yet.</div>
                        ) : (
                            <div className="space-y-2">
                                {history.map((t) => {
                                    const sent = t.direction === 'sent';
                                    return (
                                        <div key={t.id} className="card-dark p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`history-${t.id}`}>
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${sent ? 'bg-[#FF3B30]/10' : 'bg-[#39FF14]/10'}`}>
                                                    {sent ? <ArrowUpRight className="w-5 h-5 text-[#FF3B30]" /> : <ArrowDownLeft className="w-5 h-5 text-[#39FF14]" />}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="text-white font-bold truncate">
                                                        {sent ? `To ${t.recipient_name}` : `From ${t.sender_name}`}
                                                    </div>
                                                    <div className="text-[11px] text-[#A0AAB2] truncate">
                                                        {sent ? t.recipient_email : t.sender_email}
                                                        {t.memo && <span> · "{t.memo}"</span>}
                                                    </div>
                                                    <div className="text-[10px] text-[#A0AAB2]">{fmtCT(t.created_at)}</div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className={`font-mono text-lg font-black ${sent ? 'text-[#FF3B30]' : 'text-[#39FF14]'}`}>
                                                    {sent ? '−' : '+'}{fmtUSD(t.amount)}
                                                </div>
                                                <div className="mt-1 flex items-center gap-2 justify-end">
                                                    {statusBadge(t.status)}
                                                    {sent && t.status === 'pending_accept' && (
                                                        <Button size="sm" variant="outline" onClick={() => doCancel(t.id)} disabled={busyId === t.id} className="border-white/10 text-white hover:bg-white/5 text-[10px] py-0.5 px-2" data-testid={`cancel-${t.id}`}>
                                                            <RotateCcw className="w-3 h-3 mr-1" /> Cancel
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
};

export default SendCredits;
